import qs from 'qs';
import { baseApi } from './baseApi';
import {
  API_URLS,
  REQUEST_METHOD,
  API_CACHE_TIME,
  API_TAGS,
} from 'constants/global';
import { Community, Member, PERIOD } from 'constants/types';
import { getDateRange } from 'utils/url/getDateRange';
import {
  CreateCommunityPayload,
  IDBUser,
  CommunitySettingsPayload,
  EditUserPriorityPayload,
} from './types';

export const communityManagement = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createCommunity: builder.mutation<any, CreateCommunityPayload>({
      query: (data) => ({
        url: API_URLS.CREATE_COMMUNITY,
        method: REQUEST_METHOD.POST,
        data,
      }),
      invalidatesTags: [API_TAGS.communities],
    }),
    getCommunityMembers: builder.query<IDBUser, void>({
      query: () => ({
        url: API_URLS.GET_COMMUNITY_MEMBERS,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunity: builder.query<Community, void>({
      query: () => ({
        url: API_URLS.GET_COMMUNITY,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunityById: builder.query<Community, { communityId: string }>({
      query: ({ communityId }) => ({
        url: `${API_URLS.GET_COMMUNITY}/${communityId}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunityByUserId: builder.query<Community, { userId: string }>({
      query: ({ userId }) => ({
        url: `${API_URLS.GET_COMMUNITY}/${userId}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunities: builder.query<Community[], void>({
      query: () => ({
        url: API_URLS.GET_COMMUNITIES,
        method: REQUEST_METHOD.GET,
      }),
      providesTags: [API_TAGS.communities],
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getConsumers: builder.query<any, { userId?: string }>({
      query: ({ userId }) => ({
        url: `${API_URLS.GET_COMMUNITY_CONSUMERS}?${qs.stringify({
          userId,
        })}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunityPrices: builder.query<
      any,
      { prmId?: any; communityId?: string }
    >({
      query: ({ communityId, prmId }) => ({
        url: `${API_URLS.GET_COMMUNITY_PRICES}?communityId=${communityId}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunityElectricityConsumption: builder.query<any, any>({
      query: ({ startDate, endDate, communityId }) => ({
        url: `${API_URLS.GET_COMMUNITY_ELECTRICITY_CONSUMPTION}?${qs.stringify({
          startDate,
          endDate,
          communityId,
        })}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunityEarnings: builder.query<{ earnings: number }, any>({
      query: ({ startDate, endDate, communityId }) => ({
        url: `${API_URLS.GET_COMMUNITY_EARNINGS}?${qs.stringify({
          startDate,
          endDate,
          communityId,
        })}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunityInvoiceHistory: builder.query<any, void>({
      query: () => ({
        url: `${API_URLS.GET_COMMUNITY_INVOICE_HISTORY}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    getCommunityFinancialData: builder.query<any, { period: PERIOD }>({
      query: ({ period }) => ({
        url: `${API_URLS.GET_COMMUNITY_FINANCIAL_DATA}?${qs.stringify(
          getDateRange(period),
        )}`,
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    editCommunitySettings: builder.mutation<
      any,
      { payload: CommunitySettingsPayload; communityId: string }
    >({
      query: ({ payload, communityId }) => ({
        url: `${API_URLS.GET_COMMUNITY}/${communityId}/settings`,
        method: REQUEST_METHOD.PUT,
        data: payload,
      }),
      invalidatesTags: [API_TAGS.communities],
    }),
    getCommunityMembersPriority: builder.query<
      Member[],
      { communityId: string }
    >({
      query: ({ communityId }) => ({
        url: API_URLS.COMMUNITY_MEMBERS_PRIORITY(communityId),
        method: REQUEST_METHOD.GET,
      }),
      keepUnusedDataFor: API_CACHE_TIME,
    }),
    updateCommunityMembersPriority: builder.mutation<
      any,
      { communityId: string; payload: EditUserPriorityPayload }
    >({
      query: ({ communityId, payload }) => ({
        url: API_URLS.COMMUNITY_MEMBERS_PRIORITY(communityId),
        method: REQUEST_METHOD.PUT,
        data: payload,
      }),
    }),
  }),
});

export const {
  useCreateCommunityMutation,
  useGetCommunityQuery,
  useGetCommunityMembersQuery,
  useGetCommunityByIdQuery,
  useGetCommunitiesQuery,
  useGetConsumersQuery,
  useGetCommunityElectricityConsumptionQuery,
  useGetCommunityEarningsQuery,
  useGetCommunityInvoiceHistoryQuery,
  useGetCommunityFinancialDataQuery,
  useEditCommunitySettingsMutation,
  useGetCommunityByUserIdQuery,
  useGetCommunityMembersPriorityQuery,
  useUpdateCommunityMembersPriorityMutation,
} = communityManagement;

export const { resetApiState: resetCommunityApiState } =
  communityManagement.util;
